<script setup lang="ts">
import {computed} from "vue";
import Customer from "@/customer/interfaces/Customer";
import Beneficiary from "@/customer/interfaces/Beneficiary";
import {Select, SelectContent, SelectItem, SelectTrigger} from "@/core/components/ui/select";

/**
 * Pick who the flowchart is run for: the insured customer or one of their beneficiaries.
 */
const props = defineProps<{
    /** The insured customer. */
    customer: Customer;
}>();

/** The consumer key of the selected patient. */
const consumerKey = defineModel<string>({required: true});

/** The insured customer first, then their beneficiaries. */
const patients = computed((): { patient: Customer | Beneficiary, relation: string }[] => [
    {patient: props.customer, relation: 'assuré'},
    ...(props.customer.beneficiaries ?? []).map(beneficiary => ({patient: beneficiary, relation: 'ayant droit'})),
]);

const selected = computed((): Customer | Beneficiary => {
    return patients.value.find(({patient}) => patient.consumer_key === consumerKey.value)?.patient
        ?? props.customer;
});

/**
 * The display name of a patient.
 */
const fullName = (patient: Customer | Beneficiary): string => {
    return `${patient.information.first_name} ${patient.information.last_name}`;
};

/**
 * Select a patient.
 */
const select = (key: unknown) => {
    if (typeof key === 'string') {
        consumerKey.value = key;
    }
};
</script>

<template>
  <Select
    v-if="patients.length > 1"
    :model-value="selected.consumer_key"
    @update:model-value="select"
  >
    <SelectTrigger
      class="h-auto w-auto gap-1.5 rounded-full border-0 bg-primary/10 px-4 py-1.5 text-lg font-medium text-primary shadow-none transition-colors duration-200 hover:bg-primary/20 focus:ring-0"
    >
      Pour {{ fullName(selected) }}
    </SelectTrigger>
    <SelectContent>
      <SelectItem
        v-for="{patient, relation} in patients"
        :key="patient.consumer_key"
        :value="patient.consumer_key"
      >
        {{ fullName(patient) }}
        <span class="text-xs text-gray-500">
          ({{ relation }}<template v-if="patient.information.formatted_birth_date">, né(e) le {{ patient.information.formatted_birth_date }}</template>)
        </span>
      </SelectItem>
    </SelectContent>
  </Select>
  <span
    v-else
    class="rounded-full bg-primary/10 px-4 py-1.5 text-lg font-medium text-primary"
  >
    Pour {{ fullName(selected) }}
  </span>
</template>
